import { useState, useCallback } from 'react';
import {
    ConfigExportBundle,
    ImportSectionPreview,
    parseBundle,
    getImportPreview,
    importConfigSection,
} from '../../api/configTransferApi';

export type ImportPhase = 'idle' | 'loadingPreview' | 'preview' | 'importing' | 'done';

export interface SectionImportResult {
    status: 'success' | 'error';
    message: string;
}

interface UseConfigImportReturn {
    phase: ImportPhase;
    bundle: ConfigExportBundle | null;
    previews: ImportSectionPreview[];
    selected: Set<string>;
    toggleSection: (key: string) => void;
    results: Record<string, SectionImportResult>;
    /** Section currently being written during the importing phase */
    activeSection: string | null;
    error: string | null;
    selectFile: (file: File) => Promise<void>;
    runImport: () => Promise<void>;
    reset: () => void;
    /** True once at least one section has been imported successfully */
    didImport: boolean;
}

const writeErrorMessage = (err: unknown): string => {
    const message = err instanceof Error ? err.message : String(err);
    if (message.toUpperCase().includes('PERMISSION_DENIED')) {
        return 'Åtkomst nekad — kontrollera databasreglerna för denna sökväg';
    }
    return message || 'Importen misslyckades';
};

export const useConfigImport = (): UseConfigImportReturn => {
    const [phase, setPhase] = useState<ImportPhase>('idle');
    const [bundle, setBundle] = useState<ConfigExportBundle | null>(null);
    const [previews, setPreviews] = useState<ImportSectionPreview[]>([]);
    const [selected, setSelected] = useState<Set<string>>(new Set());
    const [results, setResults] = useState<Record<string, SectionImportResult>>({});
    const [activeSection, setActiveSection] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [didImport, setDidImport] = useState(false);

    const reset = useCallback(() => {
        setPhase('idle');
        setBundle(null);
        setPreviews([]);
        setSelected(new Set());
        setResults({});
        setActiveSection(null);
        setError(null);
        setDidImport(false);
    }, []);

    const selectFile = useCallback(async (file: File) => {
        setError(null);
        setPhase('loadingPreview');
        try {
            const text = await file.text();
            const parsedBundle = parseBundle(text);
            const sectionPreviews = await getImportPreview(parsedBundle);
            setBundle(parsedBundle);
            setPreviews(sectionPreviews);
            // Preselect every importable section
            setSelected(new Set(
                sectionPreviews.filter(p => p.errors.length === 0).map(p => p.key)
            ));
            setPhase('preview');
        } catch (err) {
            console.error('Error reading configuration file:', err);
            setError(err instanceof Error ? err.message : 'Filen kunde inte läsas');
            setPhase('idle');
        }
    }, []);

    const toggleSection = useCallback((key: string) => {
        setSelected(prev => {
            const next = new Set(prev);
            if (next.has(key)) next.delete(key);
            else next.add(key);
            return next;
        });
    }, []);

    const runImport = useCallback(async () => {
        if (!bundle) return;
        setPhase('importing');
        setResults({});

        const sectionsToImport = previews.filter(
            p => selected.has(p.key) && p.errors.length === 0
        );

        for (const preview of sectionsToImport) {
            setActiveSection(preview.key);
            try {
                const { written, removed } = await importConfigSection(bundle, preview.key);
                const removedSuffix = removed > 0 ? `, ${removed} befintliga togs bort` : '';
                setResults(prev => ({
                    ...prev,
                    [preview.key]: {
                        status: 'success',
                        message: `${written} poster importerade${removedSuffix}`,
                    },
                }));
                setDidImport(true);
            } catch (err) {
                console.error(`Error importing section ${preview.key}:`, err);
                setResults(prev => ({
                    ...prev,
                    [preview.key]: { status: 'error', message: writeErrorMessage(err) },
                }));
            }
        }

        setActiveSection(null);
        setPhase('done');
    }, [bundle, previews, selected]);

    return {
        phase,
        bundle,
        previews,
        selected,
        toggleSection,
        results,
        activeSection,
        error,
        selectFile,
        runImport,
        reset,
        didImport,
    };
};
